import React, { useState } from 'react';
import { TableHead, TableRow, Table, TableBody, TextField, makeStyles, Link } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import HighlightOff from '@material-ui/icons/HighlightOff';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { SmallButton } from '../util/buttons';
import { TableRowWithNote, TableCell } from '../table/table';
import { pluralise } from '../../util/string';
import { FlagIcon } from '../util/icon';

const TABLE_TYPES = {
  STARTED: 'started',
  BENCH: 'bench',
};

const SORT_COLUMNS = {
  JERSEY: 'jersey',
  NAME: 'name',
  NATIONALITY: 'nationality',
};

const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc',
};

const useStyle = makeStyles({
  change: { color: 'blue', textDecoration: 'underline' },
  required: { color: 'red', textDecoration: 'underline' },
  reducedPadding: { padding: '5px' },
  removeUnderline: { textDecoration: 'none' },
  hover: {
    '&:hover': {
      color: 'red',
      cursor: 'pointer',
    },
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  sortIcon: {
    fontSize: '14px',
    marginLeft: '4px',
    verticalAlign: 'middle',
  },
  sortIconActive: {
    color: 'red',
  },
  sortIconInactive: {
    color: '#ccc',
  },
});

const GoldenSelectionsSegregatedTable = ({
  matchData,
  createClaimFn,
  deleteLineupPlayerFn,
  selectedTeam,
  goldenRecordData,
}) => {
  const classes = useStyle();

  const [editingJersey, setEditingJersey] = useState({ playerId: 0, new: 0 });
  const [startedSort, setStartedSort] = useState({ column: null, direction: null });
  const [benchSort, setBenchSort] = useState({ column: null, direction: null });

  const handleSort = (column, table) => {
    const currentSort = table === TABLE_TYPES.STARTED ? startedSort : benchSort;
    const setSort = table === TABLE_TYPES.STARTED ? setStartedSort : setBenchSort;

    const isSameColumn = currentSort.column === column;
    const newDirection =
      isSameColumn && currentSort.direction === SORT_DIRECTIONS.ASC ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC;
    setSort({ column, direction: newDirection });
  };

  const getSortValue = (record, column) => {
    switch (column) {
      case SORT_COLUMNS.JERSEY:
        return record.lineup_jersey || 0;
      case SORT_COLUMNS.NAME:
        return record.statsbomb_player?.player_name || '';
      case SORT_COLUMNS.NATIONALITY:
        return record.statsbomb_player?.nationality_statsbomb_area?.area_name || '';
      default:
        return '';
    }
  };

  const sortData = (data, sortConfig) => {
    if (!data) return data;

    // Default sort by insertion order (created_at ascending - oldest first)
    if (!sortConfig.column || !sortConfig.direction) {
      return [...data].sort((a, b) => {
        const aCreated = new Date(a.created_at || 0);
        const bCreated = new Date(b.created_at || 0);
        return aCreated - bCreated;
      });
    }

    return [...data].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.column);
      const bValue = getSortValue(b, sortConfig.column);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === SORT_DIRECTIONS.ASC ? aValue - bValue : bValue - aValue;
      }

      return sortConfig.direction === SORT_DIRECTIONS.ASC ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
  };

  const getSortIcon = (column, sortConfig) => {
    const isActive = sortConfig.column === column;
    const isAscActive = isActive && sortConfig.direction === SORT_DIRECTIONS.ASC;
    const isDescActive = isActive && sortConfig.direction === SORT_DIRECTIONS.DESC;

    return (
      <>
        <ArrowUpwardIcon
          className={`${classes.sortIcon} ${isAscActive ? classes.sortIconActive : classes.sortIconInactive}`}
        />
        <ArrowDownwardIcon
          className={`${classes.sortIcon} ${isDescActive ? classes.sortIconActive : classes.sortIconInactive}`}
        />
      </>
    );
  };

  const isFromTeamAndType = (id, type) => record =>
    record.team_id === id && record.player_lineup_selection_type === type && !record.deleted;

  const goldenRecords = goldenRecordData
    ? {
        home: {
          startingXi: goldenRecordData.filter(isFromTeamAndType(matchData.homeTeam?.id, 'starting_xi')),
          bench: goldenRecordData.filter(isFromTeamAndType(matchData.homeTeam?.id, 'substitute')),
          deleted: goldenRecordData.filter(
            goldenRecord => goldenRecord.team_id === matchData.homeTeam?.id && goldenRecord.deleted
          ),
        },
        away: {
          startingXi: goldenRecordData.filter(isFromTeamAndType(matchData.awayTeam?.id, 'starting_xi')),
          bench: goldenRecordData.filter(isFromTeamAndType(matchData.awayTeam?.id, 'substitute')),
          deleted: goldenRecordData.filter(
            goldenRecord => goldenRecord.team_id === matchData.awayTeam?.id && goldenRecord.deleted
          ),
        },
      }
    : {};

  const submitJerseyNumberClaim = async (id, name, selectionType) => {
    await createClaimFn(id, name, selectionType, editingJersey.new);
  };

  const deletePlayerFromLineup = async id => {
    await deleteLineupPlayerFn(id);
  };

  const numberChanged = number => {
    const newEditingJerseyValue = { ...editingJersey, new: number };
    setEditingJersey(newEditingJerseyValue);
  };

  const goldenJerseyNumber = player =>
    editingJersey.playerId === player.player_id ? (
      <>
        <TextField
          onChange={e => numberChanged(e.target.value)}
          value={editingJersey.new}
          type="text"
          placeholder={player.lineup_jersey ? player.lineup_jersey.toString() : ''}
        />

        <SmallButton
          color="primary"
          onClick={() =>
            submitJerseyNumberClaim(
              player.player_id,
              player.statsbomb_player.player_name,
              player.player_lineup_selection_type
            )
          }
          text="Submit"
        />
      </>
    ) : (
      player.lineup_jersey
    );

  const goldenRecordElements = goldenRecords =>
    goldenRecords &&
    goldenRecords.map(player => (
      <TableRow key={player.player_id + player.match_id}>
        <TableCell
          data-entity-id={player.player_id}
          data-entity-type={player.player_lineup_selection_type}
          className={[
            classes.removeUnderline,
            classes.reducedPadding,
            goldenJerseyNumber(player) ? classes.change : classes.required,
          ].join(' ')}
          onClick={() => setEditingJersey({ playerId: player.player_id, new: '' })}>
          {goldenJerseyNumber(player) ? goldenJerseyNumber(player) : '+'}
        </TableCell>
        <TableCell className={classes.reducedPadding}>{player.player_id}</TableCell>
        <TableCell className={classes.reducedPadding}>
          <Link className="highlight" href={`/player?player_id=${player.player_id}`} target="_blank">
            {player.statsbomb_player.player_name}
          </Link>
        </TableCell>
        <TableCell className={classes.reducedPadding}> {player.statsbomb_player?.player_date_of_birth} </TableCell>
        <TableCell>
          <FlagIcon code={player.statsbomb_player?.nationality_statsbomb_area?.area_code} />
          {player.statsbomb_player?.nationality_statsbomb_area?.area_name}
        </TableCell>
        <TableCell className={classes.reducedPadding} style={{ textAlign: 'center' }}>
          <HighlightOff
            aria-label="delete"
            fontSize="small"
            className={classes.hover}
            onClick={() =>
              deletePlayerFromLineup(
                player.player_id,
                player.statsbomb_player.player_name,
                player.player_lineup_selection_type,
                player.lineup_jersey
              )
            }
          />{' '}
        </TableCell>
      </TableRow>
    ));

  const selectedTeamsGoldenRecordsStarted =
    selectedTeam === matchData.homeTeam?.id
      ? goldenRecordElements(sortData(goldenRecords.home?.startingXi, startedSort))
      : goldenRecordElements(sortData(goldenRecords.away?.startingXi, startedSort));

  const selectedTeamsGoldenRecordsBench =
    selectedTeam === matchData.homeTeam?.id
      ? goldenRecordElements(sortData(goldenRecords.home?.bench, benchSort))
      : goldenRecordElements(sortData(goldenRecords.away?.bench, benchSort));

  return (
    <>
      <h4>Started</h4>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              className={classes.sortableHeader}
              onClick={() => handleSort(SORT_COLUMNS.JERSEY, TABLE_TYPES.STARTED)}>
              # <EditIcon fontSize={'small'} />
              {getSortIcon(SORT_COLUMNS.JERSEY, startedSort)}
            </TableCell>
            <TableCell>ID</TableCell>
            <TableCell
              className={classes.sortableHeader}
              onClick={() => handleSort(SORT_COLUMNS.NAME, TABLE_TYPES.STARTED)}>
              Name
              {getSortIcon(SORT_COLUMNS.NAME, startedSort)}
            </TableCell>
            <TableCell> DOB </TableCell>
            <TableCell
              className={classes.sortableHeader}
              onClick={() => handleSort(SORT_COLUMNS.NATIONALITY, TABLE_TYPES.STARTED)}>
              Nationality
              {getSortIcon(SORT_COLUMNS.NATIONALITY, startedSort)}
            </TableCell>
            <TableCell>Remove from Lineup</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedTeamsGoldenRecordsStarted && selectedTeamsGoldenRecordsStarted.length > 0 ? (
            selectedTeamsGoldenRecordsStarted
          ) : (
            <TableRowWithNote note="Starting xi will appear here" />
          )}
        </TableBody>
      </Table>
      <h4>Bench</h4>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              className={classes.sortableHeader}
              onClick={() => handleSort(SORT_COLUMNS.JERSEY, TABLE_TYPES.BENCH)}>
              # <EditIcon fontSize={'small'} />
              {getSortIcon(SORT_COLUMNS.JERSEY, benchSort)}
            </TableCell>
            <TableCell>ID</TableCell>
            <TableCell
              className={classes.sortableHeader}
              onClick={() => handleSort(SORT_COLUMNS.NAME, TABLE_TYPES.BENCH)}>
              Name
              {getSortIcon(SORT_COLUMNS.NAME, benchSort)}
            </TableCell>
            <TableCell> DOB </TableCell>
            <TableCell
              className={classes.sortableHeader}
              onClick={() => handleSort(SORT_COLUMNS.NATIONALITY, TABLE_TYPES.BENCH)}>
              Nationality
              {getSortIcon(SORT_COLUMNS.NATIONALITY, benchSort)}
            </TableCell>
            <TableCell>Remove from Lineup</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {selectedTeamsGoldenRecordsBench && selectedTeamsGoldenRecordsBench.length > 0 ? (
            selectedTeamsGoldenRecordsBench
          ) : (
            <TableRowWithNote note="Bench players will appear here" />
          )}
        </TableBody>
      </Table>
    </>
  );
};

const SelectionTableRow = ({
  displayName,
  filteredRecord,
  index,
  length,
  isLoading,
  entityType,
  deleteEntityFn,
  createClaimFn,
}) => {
  const classes = useStyle();
  const borderBottom = index === length - 1 ? { padding: '5px' } : { borderBottom: 'none', padding: '5px' };
  const entityName = filteredRecord[`statsbomb_${entityType}`][`${entityType}_name`];
  const entityTypeCurrentValue = filteredRecord[`${entityType}_type`];
  const entityId = filteredRecord[`${entityType}_id`];

  const deleteClicked = ({ selectedEntityId, entityName, selectionType }) => {
    deleteEntityFn({ id: selectedEntityId });
  };

  return (
    <TableRow key={entityId}>
      {index === 0 ? (
        <TableCell style={{ ...borderBottom, fontWeight: 900 }}>{pluralise(displayName)}</TableCell>
      ) : (
        <TableCell style={borderBottom}></TableCell>
      )}
      <TableCell style={borderBottom}>
        <Link className="highlight" href={`/${entityType}?${entityType}_id=${entityId}`} target="_blank">
          {entityName}
        </Link>
      </TableCell>
      <TableCell style={borderBottom}>
        {!isLoading && (
          <HighlightOff
            data-entity-id={entityId}
            data-action="delete"
            className={classes.hover}
            fontSize="small"
            onClick={() =>
              deleteClicked({ selectedEntityId: entityId, entityName, selectionType: entityTypeCurrentValue })
            }
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const GoldenSelectionsInlineTable = ({
  goldenRecordData,
  types,
  entityType,
  headerName,
  isLoading,
  createClaimFn,
  deleteEntityFn,
}) => {
  const goldenRecordFilter = type => goldenRecordData.filter(record => record[`${entityType}_type`] === type);

  const TableSection = ({ selectionType, displayName }) => {
    const recordsForType = goldenRecordFilter(selectionType);
    const length = recordsForType?.length;
    return recordsForType.map((filteredRecord, index) => (
      <SelectionTableRow
        key={index}
        {...{ displayName, filteredRecord, index, length, isLoading, entityType, deleteEntityFn, createClaimFn }}
      />
    ));
  };

  const emptyNote = `${headerName} will appear here`;

  return (
    <>
      <h4>{headerName}</h4>
      <Table>
        <TableBody>
          {goldenRecordData && goldenRecordData[0] ? (
            types.map(({ selection_type: selectionType, display_name: displayName }) => (
              <TableSection key={selectionType} {...{ selectionType, displayName }} />
            ))
          ) : (
            <TableRowWithNote note={emptyNote} />
          )}
        </TableBody>
      </Table>
    </>
  );
};

export { GoldenSelectionsSegregatedTable, GoldenSelectionsInlineTable };
